import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantContact.css";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/tenant-contacts/`;

const emptyContact = {
  contact_type: "",
  full_name: "",
  email: "",
  phone: "",
  effective_from: "",
  effective_to: "",
  is_active: true
};

const AddTenantContact = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editContact = location.state?.editContact || null;
  const isEditMode = Boolean(editContact?.id);

  const [contacts, setContacts] = useState(() => {
    if (editContact) {
      return [
        {
          contact_type: editContact.contact_type || "",
          full_name: editContact.full_name || "",
          email: editContact.email || "",
          phone: editContact.phone || "",
          effective_from: editContact.effective_from
            ? editContact.effective_from.substring(0, 10)
            : "",
          effective_to: editContact.effective_to
            ? editContact.effective_to.substring(0, 10)
            : "",
          is_active: editContact.is_active ?? true
        }
      ];
    }

    return [{ ...emptyContact }];
  });

  const [loading, setLoading] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  const handleChange = (index, e) => {
    const { name, value, type, checked } = e.target;

    setContacts((prev) =>
      prev.map((contact, i) =>
        i === index
          ? {
              ...contact,
              [name]: type === "checkbox" ? checked : value
            }
          : contact
      )
    );
  };

  const addContact = () => {
    if (isEditMode) return;

    setContacts((prev) => [...prev, { ...emptyContact }]);
  };

  const removeContact = (index) => {
    if (isEditMode || contacts.length === 1) {
      return;
    }

    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id || !tenantUser.user_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID or User ID is missing from tenant_user."
      });
      return;
    }

    for (let i = 0; i < contacts.length; i++) {
      const contact = contacts[i];

      if (
        !contact.contact_type ||
        !contact.full_name.trim() ||
        !contact.email.trim()
      ) {
        Swal.fire({
          icon: "warning",
          title: "Required Fields Missing",
          text: `Please complete Contact Type, Full Name and Email for Contact ${
            i + 1
          }.`
        });
        return;
      }
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const contact = contacts[0];

        const payload = {
          contact_type: contact.contact_type,
          full_name: contact.full_name.trim(),
          email: contact.email.trim(),
          phone: contact.phone.trim(),
          effective_from: contact.effective_from || null,
          effective_to: contact.effective_to || null,
          is_active: contact.is_active,
          tenant: tenantUser.tenant_id,
          user: tenantUser.user_id
        };

        console.log("Update Tenant Contact Payload:", payload);

        const response = await axios.put(
          `${API_URL}${editContact.id}/`,
          payload,
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        );

        console.log("Update Tenant Contact Response:", response.data);

        await Swal.fire({
          icon: "success",
          title: "Contact Updated Successfully",
          text: "Tenant contact has been updated successfully.",
          confirmButtonText: "OK"
        });

        navigate("/admin/contacts");
        return;
      }

      const payload = contacts.map((contact) => ({
        contact_type: contact.contact_type,
        full_name: contact.full_name.trim(),
        email: contact.email.trim(),
        phone: contact.phone.trim(),
        effective_from: contact.effective_from || null,
        effective_to: contact.effective_to || null,
        is_active: contact.is_active,
        tenant: tenantUser.tenant_id,
        user: tenantUser.user_id
      }));

      console.log("Create Tenant Contacts Payload:", payload);

      const response = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json"
        }
      });

      console.log("Create Tenant Contacts Response:", response.data);

      await Swal.fire({
        icon: "success",
        title: "Contacts Created Successfully",
        text: `${contacts.length} contact${
          contacts.length > 1 ? "s" : ""
        } created successfully.`,
        confirmButtonText: "OK"
      });

      navigate("/admin/contacts");
    } catch (error) {
      console.error(
        isEditMode
          ? "Update Tenant Contact Error:"
          : "Create Tenant Contacts Error:",
        error
      );

      let errorMessage = isEditMode
        ? "Unable to update tenant contact."
        : "Unable to create tenant contacts.";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          errorMessage = Object.entries(error.response.data.errors)
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors) ? errors.join(", ") : errors
                }`
            )
            .join(" | ");
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      Swal.fire({
        icon: "error",
        title: isEditMode ? "Update Failed" : "Save Failed",
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/contacts");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-contact-page">
          <div className="tenant-contact-page-header">
            <div>
              <h2>{isEditMode ? "Edit Contact" : "Create Contact"}</h2>
              <p>
                {isEditMode
                  ? "Update tenant contact information."
                  : "Add one or multiple tenant contacts."}
              </p>
            </div>

            <button
              type="button"
              className="tenant-contact-back-btn"
              onClick={handleCancel}
            >
              ← Back
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="tenant-contact-card">
              <div className="tenant-contact-form-body">
                {contacts.map((contact, index) => (
                  <div className="tenant-contact-record" key={index}>
                    <div className="tenant-contact-record-header">
                      <div>
                        <h4>
                          {isEditMode ? "Contact Details" : `Contact ${index + 1}`}
                        </h4>
                      </div>

                      {!isEditMode && contacts.length > 1 && (
                        <button
                          type="button"
                          className="tenant-contact-remove-btn"
                          onClick={() => removeContact(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="tenant-contact-form-grid">
                      <div className="tenant-contact-form-group">
                        <label>
                          Contact Type <span>*</span>
                        </label>

                        <select
                          name="contact_type"
                          value={contact.contact_type}
                          onChange={(e) => handleChange(index, e)}
                          required
                        >
                          <option value="">Select contact type</option>
                          <option value="ORG_ADMIN">
                            Organisation Admin
                          </option>
                          <option value="TECHNICAL">Technical</option>
                          <option value="PROJECT">Project</option>
                          <option value="FINANCE">Finance</option>
                          <option value="LEGAL">Legal</option>
                          <option value="SECURITY">Security</option>
                        </select>
                      </div>

                      <div className="tenant-contact-form-group">
                        <label>
                          Full Name <span>*</span>
                        </label>

                        <input
                          type="text"
                          name="full_name"
                          value={contact.full_name}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="Enter full name"
                          maxLength={255}
                          required
                        />
                      </div>

                      <div className="tenant-contact-form-group">
                        <label>
                          Email <span>*</span>
                        </label>

                        <input
                          type="email"
                          name="email"
                          value={contact.email}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="Enter email address"
                          required
                        />
                      </div>

                      <div className="tenant-contact-form-group">
                        <label>Phone</label>

                        <input
                          type="tel"
                          name="phone"
                          value={contact.phone}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="Enter phone number"
                          maxLength={20}
                        />
                      </div>

                      <div className="tenant-contact-form-group">
                        <label>Effective From</label>

                        <input
                          type="date"
                          name="effective_from"
                          value={contact.effective_from}
                          onChange={(e) => handleChange(index, e)}
                        />
                      </div>

                      <div className="tenant-contact-form-group">
                        <label>Effective To</label>

                        <input
                          type="date"
                          name="effective_to"
                          value={contact.effective_to}
                          onChange={(e) => handleChange(index, e)}
                        />
                      </div>

                      <div className="tenant-contact-status-field">
                        <label>Contact Status</label>

                        <label className="tenant-contact-switch">
                          <input
                            type="checkbox"
                            name="is_active"
                            checked={contact.is_active}
                            onChange={(e) => handleChange(index, e)}
                          />
                          <span className="tenant-contact-slider"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditMode && (
                  <button
                    type="button"
                    className="tenant-contact-add-btn"
                    onClick={addContact}
                  >
                    <span>+</span>
                    Add Another Contact
                  </button>
                )}
              </div>
            </div>

            <div className="tenant-contact-form-actions">
              <button
                type="button"
                className="tenant-contact-cancel-btn"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="tenant-contact-save-btn"
                disabled={loading}
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Saving..."
                  : isEditMode
                  ? "Update Contact"
                  : `Save ${contacts.length} Contact${
                      contacts.length > 1 ? "s" : ""
                    }`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTenantContact;